"use client"

import { Loader2 } from "lucide-react"
import { JSX, memo, useCallback } from "react"
import { useDataAttributes } from "../../hooks"
import { BaseInteractiveProps } from "../../utils/ComponentProps"
import { TEditorIcon } from "../display/TEditorIcon"
import { TIconName } from "../display/TIconPaths"
import { IconSelectTrigger, Select, SelectContent, SelectGroup, SelectItem } from "../shadcn/select"
import { IDropdownProps, ISelectionProps } from "./types"

/**
 * 에디터 아이콘 셀렉트 컴포넌트
 *
 * @description
 * 에디터용 아이콘만 표시하는 드롭다운 셀렉트 컴포넌트입니다.
 * TIconSelect와 동일하지만 TEditorIcon을 사용합니다.
 * 에디터 툴바에서 사용하기 위한 컴포넌트입니다.
 *
 * @template V - 값 타입 (string 또는 number)
 *
 * @example
 * ```tsx
 * // 에디터 폰트 선택
 * <TEditorIconSelect
 *   name="font"
 *   items={[
 *     { value: "arial", label: "Arial" },
 *     { value: "georgia", label: "Georgia" },
 *     { value: "times", label: "Times New Roman" }
 *   ]}
 *   value={font}
 *   onChange={setFont}
 * />
 *
 * // 에디터 정렬 선택
 * <TEditorIconSelect
 *   name="alignLeft"
 *   items={[
 *     { value: "left", label: "왼쪽 정렬" },
 *     { value: "center", label: "가운데 정렬" },
 *     { value: "right", label: "오른쪽 정렬" }
 *   ]}
 *   value={align}
 *   onChange={setAlign}
 * />
 * ```
 */
interface TEditorIconSelectProps<V extends string | number> extends ISelectionProps<V>, IDropdownProps, BaseInteractiveProps {
	/** 표시할 에디터 아이콘 이름 */
	name?: TIconName
	/** 아이콘 버튼 크기 */
	size?: "small" | "medium"
}

function TEditorIconSelectInner<V extends string | number>(props: TEditorIconSelectProps<V>): JSX.Element {
	const {
		// 기본 속성
		id,
		className,

		// 데이터 관련
		items,
		value,

		// 상태 관련
		disabled = false,
		loading = false,

		// UI 관련
		name = "filter",
		size = "small",

		// 드롭다운 설정
		open,
		align = "start",
		side = "bottom",
		alignOffset = 0,
		sideOffset = 4,

		// 이벤트 핸들러
		onChange,
		onOpenChange,

		// 기타
		dataAttributes,
		...restProps
	} = props

	const dataProps = useDataAttributes(dataAttributes)

	const handleSelect = useCallback(
		(stringValue: string) => {
			const item = items.find((item) => String(item.value) === stringValue)
			if (item === undefined) return

			onChange(item.value)
		},
		[items, onChange]
	)

	const selectedValue = value !== undefined ? String(value) : undefined
	const hasItems = items.length > 0
	const isDisabled = loading || disabled || !hasItems

	// 개발 환경에서 빈 items와 value prop 불일치 경고
	if (process.env.NODE_ENV === "development" && !hasItems && value !== undefined) {
		console.warn(`TEditorIconSelect: 'value' prop is provided (${value}) but 'items' is empty. The value will be ignored until items are provided.`)
	}

	return (
		<div id={id} className={className} {...restProps}>
			<Select value={selectedValue} onValueChange={handleSelect} disabled={isDisabled} open={open} onOpenChange={onOpenChange} {...dataProps}>
				<IconSelectTrigger disabled={isDisabled} data-size={size}>
					{loading ? <Loader2 aria-hidden="true" className="loader" /> : <TEditorIcon className="icon" name={name} size={20} />}
				</IconSelectTrigger>
				<SelectContent align={align} alignOffset={alignOffset} side={side} sideOffset={sideOffset} position="popper">
					<SelectGroup>
						{items.map((item) => (
							<SelectItem key={String(item.value)} value={String(item.value)} disabled={item.disabled}>
								{item.label}
							</SelectItem>
						))}
					</SelectGroup>
				</SelectContent>
			</Select>
		</div>
	)
}

const TEditorIconSelectMemo = memo(TEditorIconSelectInner)
TEditorIconSelectMemo.displayName = "TEditorIconSelect"

export const TEditorIconSelect = TEditorIconSelectMemo as typeof TEditorIconSelectInner
